/**
 * Theme registry: the single place themes are declared.
 *
 * Every theme has:
 * - `colors`: CSS custom properties applied to the document root (keys without the `--` prefix,
 *   matching the variables in index.css)
 * - `texts`: verdict headlines shown by the main app
 *
 * Optional:
 * - `page`: a bespoke layout route module (path relative to `src/`) rendered at `/<key>` instead
 *   of the main app
 * - `unlisted`: keep out of search engines, the sitemap and navigation (e.g. sponsor demos)
 *
 * `src/routes.js` turns every theme into a route: `/<key>` renders the theme's `page` if it has
 * one, otherwise the main app in that palette. Every theme route is prerendered.
 *
 * Plain data only (no imports): the route config and build config load this file in Node.
 */
export const themes = {
    default: {
        colors: {
            // Light/Day mode colors
            'color-day-bg': '#fdfbf7',
            'color-day-text': '#2c3e50',
            'color-day-accent': '#e67e22',

            // Dark/Pub mode colors
            'color-pub-bg': '#2c3e50',
            'color-pub-text': '#ecf0f1',
            'color-pub-accent': '#f1c40f',

            // Gradients
            'gradient-day': 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
            'gradient-pub': 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)',

            // Shared button colors (will be used with rgba in CSS, so we store the rgb values)
            'rgb-day-accent': '230, 126, 34', // #e67e22
            'rgb-pub-accent': '241, 196, 15', // #f1c40f

            // Park/Pub specific
            'color-park-text': '#2c3e50',
            'color-park-feature': '#27ae60',
            'rgb-park-feature': '39, 174, 96', // #27ae60
        },
        texts: {
            dayTitle: "YEAH, IT'S A DAY FOR IT!",
            notDayTitle: "NAH, NOT A DAY FOR IT."
        }
    },

    // Sponsor demo: unlisted until a deal exists.
    woolies: {
        unlisted: true,
        colors: {
            // Light/Day mode colors (Woolies Green Theme)
            'color-day-bg': '#f3f9f4',
            'color-day-text': '#173303', // Dark green text
            'color-day-accent': '#178a00', // Woolies Green

            // Dark/Pub mode colors (Slightly darker green/black for contrast)
            'color-pub-bg': '#0e2b05',
            'color-pub-text': '#ecf0f1',
            'color-pub-accent': '#6ccf44', // Lighter green accent

            // Gradients
            'gradient-day': 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)', // Fresh green gradient
            'gradient-pub': 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)', // Dark watery/night gradient

            // Shared button colors
            'rgb-day-accent': '23, 138, 0', // #178a00
            'rgb-pub-accent': '108, 207, 68', // #6ccf44

            // Park/Pub specific
            'color-park-text': '#173303',
            'color-park-feature': '#178a00',
            'rgb-park-feature': '23, 138, 0',
        },
        texts: {
            dayTitle: "yeah its a day for it, grab a few tins at woolies to enjoy it",
            notDayTitle: "NAH, NOT A DAY FOR IT."
        }
    },

    brunswick: {
        page: 'pages/BrunswickPage.jsx',
        colors: {
            // Light/Day mode colors (Natty Wine Grunge)
            'color-day-bg': '#ecebe8', // Off-white
            'color-day-text': '#1a1a1a', // Matte black
            'color-day-accent': '#ff4d00', // Bright orange

            // Dark/Pub mode colors (Dim lit pub)
            'color-pub-bg': '#121212',
            'color-pub-text': '#e0e0e0',
            'color-pub-accent': '#aa00ff', // Vivid purple

            // Gradients
            'gradient-day': 'linear-gradient(135deg, #e4e3df 0%, #dbdad5 100%)',
            'gradient-pub': 'linear-gradient(135deg, #0d0d0d 0%, #171717 100%)',

            // Shared button colors
            'rgb-day-accent': '255, 77, 0',
            'rgb-pub-accent': '170, 0, 255',

            // Park/Pub specific
            'color-park-text': '#1a1a1a',
            'color-park-feature': '#ff4d00',
            'rgb-park-feature': '255, 77, 0',
        },
        texts: {
            dayTitle: "yeah nah it's a day for it. grab a natty wine.",
            notDayTitle: "nah weather's cooked. dim light pub time."
        }
    },

    'client-liaison': {
        page: 'pages/ClientLiaisonPage.jsx',
        colors: {
            // Light/Day mode colors (Pastel 80s Corporate)
            'color-day-bg': '#ffebf0', // Pastel pink
            'color-day-text': '#0033aa', // Bright blue
            'color-day-accent': '#ff007f', // Hot pink

            // Dark/Pub mode colors (Teal/Yellow 80s Night)
            'color-pub-bg': '#00c4b5', // Teal
            'color-pub-text': '#330066', // Deep purple
            'color-pub-accent': '#ffe100', // Bright yellow

            // Gradients
            'gradient-day': 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)',
            'gradient-pub': 'linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%)',

            // Shared button colors
            'rgb-day-accent': '255, 0, 127',
            'rgb-pub-accent': '255, 225, 0',

            // Park/Pub specific
            'color-park-text': '#0033aa',
            'color-park-feature': '#ff007f',
            'rgb-park-feature': '255, 0, 127',
        },
        texts: {
            dayTitle: "IT'S A RIPPER DAY FOR IT! CHUCK ON SOME PASH RASH!",
            notDayTitle: "YEAH NAH MATE. OFF TO THE RSL."
        }
    },

    premium: {
        page: 'pages/PremiumPage.jsx',
        colors: {
            // Light/Day mode colors (Sleek Dark Glassmorphism)
            'color-day-bg': '#0b0c10', // Extremely dark blue/black
            'color-day-text': '#ffffff', // Crisp white
            'color-day-accent': '#66fcf1', // Neon cyan

            // Dark/Pub mode colors (Void Black)
            'color-pub-bg': '#000000',
            'color-pub-text': '#e0e0e0',
            'color-pub-accent': '#45a29e', // Muted cyan

            // Gradients (Vibrant glowing gradients mixed with dark background)
            'gradient-day': 'linear-gradient(135deg, #130026 0%, #300028 50%, #7d0037 100%)',
            'gradient-pub': 'linear-gradient(135deg, #050505 0%, #111111 50%, #1a1a2e 100%)',

            // Shared button colors
            'rgb-day-accent': '102, 252, 241',
            'rgb-pub-accent': '69, 162, 158',

            // Park/Pub specific
            'color-park-text': '#ffffff',
            'color-park-feature': '#66fcf1',
            'rgb-park-feature': '102, 252, 241',
        },
        texts: {
            dayTitle: "Optimal conditions detected. Proceed outdoors.",
            notDayTitle: "Suboptimal climate. Seek indoor shelter."
        }
    }
};

export const DEFAULT_THEME = 'default';

export const getTheme = (key) => themes[key] || themes[DEFAULT_THEME];
